import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

type State = "loading" | "valid" | "already" | "invalid" | "submitting" | "done" | "error";

const UnsubscribePage = () => {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<State>("loading");
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const anon = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
    fetch(`${supabaseUrl}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`, {
      headers: { apikey: anon },
    })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (r.ok && body.valid) {
          setEmail(body.email ?? null);
          setState(body.alreadyUsed ? "already" : "valid");
        } else {
          setState("invalid");
        }
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const confirm = async () => {
    setState("submitting");
    const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
      body: { token },
    });
    if (error || (data as any)?.error) setState("error");
    else setState("done");
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container max-w-md py-16 text-center">
        <h1 className="font-serif text-3xl text-foreground mb-3">Unsubscribe</h1>
        {state === "loading" && <p className="text-muted-foreground">Checking your link…</p>}
        {state === "invalid" && (
          <p className="text-muted-foreground">
            This unsubscribe link is invalid or expired.
          </p>
        )}
        {state === "already" && (
          <p className="text-muted-foreground">
            {email ?? "This address"} is already unsubscribed.
          </p>
        )}
        {state === "valid" && (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Unsubscribe {email ?? "this address"} from vecurate emails?
            </p>
            <Button onClick={confirm} className="rounded-lg">Confirm unsubscribe</Button>
          </div>
        )}
        {state === "submitting" && <p className="text-muted-foreground">Unsubscribing…</p>}
        {state === "done" && (
          <p className="text-muted-foreground">You're unsubscribed. Sorry to see you go.</p>
        )}
        {state === "error" && (
          <p className="text-destructive">Something went wrong. Please try again later.</p>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default UnsubscribePage;
